// Settings → Appearance: how the app looks, in the order a person thinks of it. One picker
// (components/ThemePicker.tsx, docs/plans/theme-layers.md): this device's say, mine, and the
// workspace's, each a theme and a brightness. Which suite's layers the Me and This workspace tabs
// edit (Everything or a suite; a suite's own Edit sheet writes the workspace's) sits above the
// picker; then whatever the caller adds (label colours, app icon).
import { useId, useState, type ReactNode } from 'react'
import { useSettings, useWorkspaces } from '@/db/hooks'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { orderSuites } from '@/suites/switch'
import { personEntryLayer, type PersonLook } from '@/lib/look'
import { layerOfCarrier } from '@/lib/themeLayers'
import { useAccess } from '@/state/access'
import { tokens, type Hue } from '@/lib/colour'
import { ThemePicker } from '@/components/ThemePicker'
import { CodeLookSettings } from '@/components/CodeLookSettings'
import { cx } from '@/components/ui'

export const AppearanceSettings = ({ children }: { children?: ReactNode }) => {
  const settings = useSettings()
  const suites = orderSuites(useWorkspaces())
  const scope = useScope((s) => s.ws)
  // start on the scope you are in; an unknown slug (a removed suite) means Everything
  const [picked, setPicked] = useState<string>(scope)
  const suite = suites.find((w) => w.spec.slug === picked)
  const target = suite ? suite.spec.slug : ALL
  const admin = useAccess((s) => s.admin)
  const scopeLabel = useId()
  const mine = settings.person_look as PersonLook | undefined

  const chip = (key: string, label: string, accent: string) => (
    <button
      key={key}
      type="button"
      role="radio"
      aria-checked={target === key}
      onClick={() => setPicked(key)}
      className={cx(
        'shrink-0 flex items-center gap-2 rounded-full border px-3 min-h-9 text-sm',
        target === key ? 'border-accent bg-accent-soft text-fg' : 'border-line text-muted',
      )}
      data-testid={`look-scope-${key === ALL ? 'all' : key}`}
    >
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: accent }} />
      {label}
    </button>
  )
  const accentOf = (hue: unknown) => tokens(((hue as Hue | undefined) ?? 'violet') as Hue).accent

  return (
    <div className="flex flex-col gap-6" data-testid="appearance-settings">
      <section data-testid="theme-settings">
        <h2 className="px-1 pb-1.5 text-[13px] font-semibold text-muted">Theme</h2>
        <p className="text-xs text-faint px-1 mb-2">
          Each look is a theme and a brightness. This device’s wins over mine, mine over the workspace’s.
        </p>
        <div className="px-1 pb-1 text-xs text-muted" id={scopeLabel}>
          Mine and the workspace’s are for
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 mb-2" role="radiogroup" aria-labelledby={scopeLabel}>
          {chip(
            ALL,
            admin ? 'All my suites' : 'Everything',
            accentOf(
              admin
                ? personEntryLayer(mine, null).colour
                : (layerOfCarrier(settings.look).colour ?? undefined),
            ),
          )}
          {suites.map((w) => chip(w.spec.slug, w.spec.name, tokens(w.spec.colour as Hue).accent))}
        </div>
        <ThemePicker target={target} />
      </section>
      <CodeLookSettings />
      {children}
    </div>
  )
}
